import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { readSharedToken } from '../utils/authTokenKeys';
import { authHeaders, resolveOrgId } from '../../utils/authHeaders';
import { getOfflineActions, saveOfflineAction } from '../utils/offlineStorage';
import { scopeHeaders } from '../utils/scopeHeaders';
import { tone } from '../utils/tone';
import { apiBase } from '../../utils/authHeaders';
import { 
  Package, Search, Camera, AlertTriangle, Plus, Minus,
  Barcode, Clock, TrendingDown, CheckCircle, Filter, ClipboardList,
  ChevronRight, Save, X, Truck
} from 'lucide-react';

const MobileInventory = () => {
  const location = useLocation();
  const { user, token, role, isOnline, hasPermission } = useMobileAuth();
  const [activeMode, setActiveMode] = useState('list'); // 'list', 'stocktake', 'receive'
  const [items, setItems] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receiveCounts, setReceiveCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showScanner, setShowScanner] = useState(false);
  const [offlineActions, setOfflineActions] = useState([]);
  
  // Stock Take State
  const [stockTakeSession, setStockTakeSession] = useState(null);
  const [stockTakeCounts, setStockTakeCounts] = useState({});

  useEffect(() => {
    fetchInventory();
    fetchPurchaseOrders();
    loadOfflineActions();

    if (location.state?.action === 'scan') {
        setShowScanner(true);
    }
  }, [location.state]);

  const fetchInventory = async () => {
    try {
      const token = readSharedToken();
      const response = await fetch(`${apiBase()}/api/mobile/inventory`, {
        headers: authHeaders({ token, locationId: localStorage.getItem('current_location_id') })
      });

      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const token = readSharedToken();
      const response = await fetch(`${apiBase()}/api/mobile/inventory/purchase-orders`, {
        headers: authHeaders({ token, locationId: localStorage.getItem('current_location_id') })
      });
      if (response.ok) {
        const data = await response.json();
        setPurchaseOrders(data);
      }
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
    }
  };

  const loadOfflineActions = async () => {
    try {
      const actions = await getOfflineActions();
      setOfflineActions(actions.filter(action => action.type === 'inventory'));
    } catch (error) {
      console.error('Error loading offline actions:', error);
    }
  };

  // ... (keep existing scanner and update logic) ...
  const handleBarcodeScan = async (barcode) => {
    // 1. Find item by barcode/SKU
    const scannedItem = items.find(i => i.sku === barcode || i.id === barcode);
    
    if (scannedItem) {
      if (activeMode === 'stocktake') {
        // Increment count for this item
        const currentCount = stockTakeCounts[scannedItem.id]?.counted || 0;
        submitStockCount(scannedItem.id, currentCount + 1);
        alert(`Scanned ${scannedItem.name}. Count: ${currentCount + 1}`);
      } else {
        // Show item details or highlight in list
        setSearchTerm(scannedItem.name);
        // Optional: scroll to item
      }
    } else {
      alert('Item not found');
    }
  };

  const updateStock = async (itemId, change, reason) => {
    const action = {
        id: Date.now().toString(),
        type: 'inventory',
        subType: 'update_stock',
        itemId,
        change,
        reason,
        timestamp: new Date().toISOString(),
        synced: false
    };

    if (isOnline) {
        try {
            
            await fetch(`${apiBase()}/api/mobile/inventory/update`, {
                method: 'POST',
                headers: {
                    ...scopeHeaders(token),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ itemId, change, reason })
            });
            
            // Optimistic update
            setItems(items.map(item => {
                if (item.id === itemId) {
                    return { ...item, available_quantity: item.available_quantity + change };
                }
                return item;
            }));
        } catch (error) {
            console.error('Error updating stock:', error);
            saveOfflineAction(action);
            setOfflineActions([...offlineActions, action]);
        }
    } else {
        saveOfflineAction(action);
        setOfflineActions([...offlineActions, action]);
        // Offline optimistic update
        setItems(items.map(item => {
            if (item.id === itemId) {
                return { ...item, available_quantity: item.available_quantity + change };
            }
            return item;
        }));
    }
  };
  
  // Stock Take Functions
  const startStockTake = () => {
    setActiveMode('stocktake');
    setStockTakeSession({
      id: Date.now().toString(),
      startTime: new Date().toISOString(),
      itemsCounted: 0,
      totalItems: items.length
    });
    // Initialize counts with current known quantities (or 0 if blind count)
    const initialCounts = {};
    items.forEach(item => {
      initialCounts[item.id] = {
        counted: item.available_quantity, // defaulting to current for ease, could be 0
        status: 'pending' // pending, counted, skipped
      };
    });
    setStockTakeCounts(initialCounts);
  };

  const submitStockCount = (itemId, quantity) => {
    setStockTakeCounts(prev => ({
      ...prev,
      [itemId]: {
        counted: quantity,
        status: 'counted'
      }
    }));
    
    setStockTakeSession(prev => ({
      ...prev,
      itemsCounted: Object.values(stockTakeCounts).filter(c => c.status === 'counted').length + 1
    }));
  };

  const finishStockTake = async () => {
    // Generate adjustments for all differences
    const adjustments = [];
    Object.entries(stockTakeCounts).forEach(([itemId, data]) => {
      const item = items.find(i => i.id === itemId);
      if (item && data.status === 'counted' && data.counted !== item.available_quantity) {
        adjustments.push({
          itemId,
          oldQuantity: item.available_quantity,
          newQuantity: data.counted,
          difference: data.counted - item.available_quantity
        });
      }
    });

    if (adjustments.length > 0) {
      // Batch update or sequential updates
      for (const adj of adjustments) {
        await updateStock(adj.itemId, adj.difference, 'stocktake_adjustment');
      }
    }

    setActiveMode('list');
    setStockTakeSession(null);
    setStockTakeCounts({});
    alert(`Stock take completed. ${adjustments.length} adjustments made.`);
  };

  const receivePurchaseOrder = async (poId, itemsReceived) => {
    try {
        const token = readSharedToken();
        const po = purchaseOrders.find(order => order.id === poId);
        const site = po?.location_id || localStorage.getItem('current_location_id');
        if (!site) throw new Error('Select a site before receiving.');
        const storageKey = JSON.stringify(['receiving-retry', user.id, resolveOrgId(), site, poId]);
        const items = JSON.stringify(itemsReceived);
        const pending = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (pending && pending.items !== items) throw new Error('Retry the pending receipt before changing its quantities.');
        const receiptId = pending?.id || crypto.randomUUID();
        localStorage.setItem(storageKey, JSON.stringify({ id: receiptId, items }));
        const response = await fetch(`${apiBase()}/api/mobile/inventory/purchase-orders/${poId}/receive`, {
            method: 'POST',
            headers: authHeaders({ token, locationId: site, json: true }),
            body: JSON.stringify({ items: itemsReceived, receipt_id: receiptId })
        });

        if (response.ok) {
            localStorage.removeItem(storageKey);
            alert('Purchase Order received successfully');
            fetchInventory(); // Refresh inventory levels
            fetchPurchaseOrders();
            setActiveMode('list');
        } else {
            if ([400, 403, 404, 422].includes(response.status)) localStorage.removeItem(storageKey);
            const error = await response.json();
            throw new Error(typeof error.detail === 'string' ? error.detail : 'Receipt was not accepted.');
        }
    } catch (error) {
        console.error('Error receiving PO:', error);
        alert(error.message || 'Could not receive this purchase order.');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // ... (keep existing helper functions) ...
  const getStockStatus = (item) => {
      const percentage = (item.available_quantity / item.total_quantity) * 100;
      if (percentage <= 10) return { status: 'critical', color: 'red', icon: AlertTriangle };
      if (percentage <= 25) return { status: 'low', color: 'orange', icon: TrendingDown };
      if (percentage <= 50) return { status: 'medium', color: 'yellow', icon: Clock };
      return { status: 'good', color: 'green', icon: CheckCircle };
  };

  const getEmployeeActions = (item) => {
    const actions = [];
    
    if (hasPermission('canManageInventory')) {
        actions.push({
          name: 'Add Stock',
          icon: Plus,
          color: 'green',
          action: () => updateStock(item.id, +1, 'stock_in')
        });
        
        actions.push({
          name: 'Remove Stock',
          icon: Minus,
          color: 'red',
          action: () => updateStock(item.id, -1, 'stock_out')
        });
    }

    // ...
    return actions;
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div></div>;

  // Stock Take View
  if (activeMode === 'stocktake') {
    const pendingItems = filteredItems.filter(item => stockTakeCounts[item.id]?.status !== 'counted');
    const countedItems = filteredItems.filter(item => stockTakeCounts[item.id]?.status === 'counted');

    return (
      <div className="min-h-screen bg-surface-muted pb-20">
        <div className="bg-surface shadow-sm sticky top-0 z-10 p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="font-display text-xl font-bold text-ink">Stock Take</h1>
            <button 
              onClick={() => setActiveMode('list')}
              className="text-ink-muted hover:text-ink"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex items-center justify-between bg-primary-50 p-3 rounded-lg">
            <div>
              <p className="text-sm text-primary-800 font-medium">Progress</p>
              <p className="font-display text-2xl font-bold text-primary-900">
                {Math.round((stockTakeSession.itemsCounted / items.length) * 100)}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-primary-800">{stockTakeSession.itemsCounted} / {items.length}</p>
              <p className="text-xs text-primary-600">items counted</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {pendingItems.map(item => (
            <div key={item.id} className="bg-surface rounded-lg shadow-sm p-4 border-l-4 border-warn">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-ink">{item.name}</h3>
                  <p className="text-sm text-ink-muted">{item.sku}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-ink-subtle">System Qty</span>
                  <p className="font-mono text-ink-muted">{item.available_quantity} {item.unit}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  placeholder="Count"
                  className="flex-1 px-4 py-2 border border-surface-border rounded-lg text-lg text-center"
                  defaultValue={stockTakeCounts[item.id]?.counted}
                  onBlur={(e) => submitStockCount(item.id, parseFloat(e.target.value))}
                />
                <button
                  onClick={() => setShowScanner(true)}
                  className="p-3 bg-surface-sunken rounded-lg text-ink-muted"
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}

          {pendingItems.length === 0 && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-primary-500 mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold text-ink">All items counted!</h3>
              <p className="text-ink-muted mb-6">Review your changes and submit.</p>
              <button
                onClick={finishStockTake}
                className="w-full py-3 bg-primary-500 text-white rounded-lg font-bold shadow-lg"
              >
                Complete Stock Take
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-muted pb-20">
      {/* Header */}
      <div className="bg-surface shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3 flex justify-between items-center">
          <div>
            <h1 className="font-display text-xl font-bold text-ink">Inventory</h1>
            <p className="text-sm text-ink-muted">Manage stock levels</p>
          </div>
          {hasPermission('canManageInventory') && (
            <button
              onClick={startStockTake}
              className="flex items-center space-x-1 px-3 py-2 bg-primary-100 text-primary-700 rounded-lg text-sm font-medium"
            >
              <ClipboardList className="w-4 h-4" />
              <span>Stock Take</span>
            </button>
          )}
        </div>
        
        {/* Search and Filter */}
        <div className="px-4 pb-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-subtle" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-surface-border rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowScanner(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              <Camera className="w-4 h-4" />
              <span>Scan</span>
            </button>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex-1 px-4 py-2 border border-surface-border rounded-lg focus:ring-2 focus:ring-primary-300"
            >
              <option value="all">All Categories</option>
              <option value="food">Food</option>
              <option value="beverage">Beverages</option>
              <option value="supplies">Supplies</option>
            </select>
          </div>
        </div>
      </div>

      {/* Offline Sync Status */}
      {!isOnline && offlineActions.length > 0 && (
        <div className="bg-warn/10 border-b border-warn/40 px-4 py-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-accent-800">
              {offlineActions.length} changes will sync when online
            </p>
            <button
              onClick={() => {/* sync logic */}}
              className="text-sm text-accent-600 underline"
            >
              Sync Now
            </button>
          </div>
        </div>
      )}

      {/* Items List */}
      <div className="px-4 py-4 space-y-3">
        {filteredItems.map(item => {
          const stockStatus = getStockStatus(item);
          const StatusIcon = stockStatus.icon;
          
          return (
            <div key={item.id} className="bg-surface rounded-lg shadow-sm overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-ink">{item.name}</h3>
                      <StatusIcon className={`w-4 h-4 ${tone(stockStatus.color).icon}`} />
                    </div>
                    <p className="text-sm text-ink-muted">{item.sku}</p>
                    <p className="text-xs text-ink-subtle">{item.category}</p>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-display text-lg font-bold text-ink">
                      {item.available_quantity} {item.unit}
                    </p>
                    <p className="text-xs text-ink-muted">
                      {item.total_quantity} total
                    </p>
                  </div>
                </div>

                {/* Stock Level Indicator */}
                <div className="mt-3">
                  <div className="w-full bg-surface-border rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${tone(stockStatus.color).bar}`}
                      style={{ width: `${(item.available_quantity / item.total_quantity) * 100}%` }}
                    ></div>
                  </div>
                </div>

                {/* Alerts */}
                {item.expiry_date && (
                  <div className="mt-2 flex items-center text-xs">
                    <Clock className="w-3 h-3 text-accent-600 mr-1" />
                    <span className="text-accent-600">
                      Expires: {new Date(item.expiry_date).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {/* Employee Actions */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {getEmployeeActions(item).map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={index}
                        onClick={action.action}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${tone(action.color).chip}`}
                      >
                        <Icon className="w-3 h-3" />
                        <span>{action.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        {filteredItems.length === 0 && (
          <div className="text-center py-8">
            <Package className="w-12 h-12 text-ink-subtle mx-auto mb-4" />
            <p className="text-ink-muted">No items found</p>
          </div>
        )}
      </div>

      {/* Barcode Scanner Modal (reuse existing) */}
      {showScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-surface rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">Scan Barcode</h3>
            <div className="bg-surface-sunken rounded-lg p-8 text-center">
              <Barcode className="w-16 h-16 text-ink-subtle mx-auto mb-4" />
              <p className="text-ink-muted">Camera scanner would appear here</p>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowScanner(false)}
                className="px-4 py-2 text-ink-muted border border-surface-border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Manual entry until native camera scanning is integrated —
                  // captures a real barcode, never a fabricated value.
                  const code = window.prompt('Enter barcode / SKU:');
                  if (code && code.trim()) {
                    handleBarcodeScan(code.trim());
                  }
                  setShowScanner(false);
                }}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg"
              >
                Enter Barcode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileInventory;
